'use client';

import { useState } from 'react';

export default function Footer() {
  const currentYear = new Date()?.getFullYear();
  const [copied, setCopied] = useState(false);
  const supportEmail = 'support@vantarust.com';

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard?.writeText(supportEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  return (
    <footer className="border-t border-white/5 py-16 px-6">
      <div className="max-w-7xl mx-auto flex flex-col items-center gap-12">
        {/* Support Email Section */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
          <button
            onClick={handleCopyEmail}
            className="px-6 py-3 rounded-lg bg-[#6a5cff]/10 border border-[#6a5cff]/30 text-[#6a5cff] font-medium hover:bg-[#6a5cff]/20 hover:border-[#6a5cff]/50 transition-all duration-200 flex items-center gap-2 min-w-[200px] justify-center"
            aria-label="Copy support email to clipboard"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy Support Email</span>
              </>
            )}
          </button>
          
          <a
            href={`mailto:${supportEmail}`}
            className="px-6 py-3 rounded-lg glass text-vanta-text font-medium hover:text-white hover:border-[#6a5cff]/30 transition-all duration-200 flex items-center gap-2 min-w-[200px] justify-center"
            aria-label="Contact support via email"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Contact Support</span>
          </a>
        </div>

        {/* Existing Footer Content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 w-full">
          {/* Logo + copyright */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6" aria-hidden="true">
                <polygon points="16,4 30,28 2,28" fill="none" stroke="#6a5cff" strokeWidth="2.5" strokeLinejoin="round" />
                <polygon points="16,11 24,28 8,28" fill="rgba(106,92,255,0.15)" stroke="none" />
              </svg>
              <span className="font-heading font-800 tracking-[0.12em] text-vanta-text text-sm" style={{ fontWeight: 800 }}>
                VANTA
              </span>
              <span className="text-vanta-dim text-sm">© 2026 Vanta.</span>
            </div>
            <span className="text-vanta-dim text-xs ml-9">Not affiliated with Facepunch Studios.</span>
          </div>

          {/* Links */}
          <div className="flex items-center gap-8 text-sm font-medium text-vanta-muted">
            <a href="/home#servers" className="hover:text-white transition-colors">Servers</a>
            <a href="/leaderboards" className="hover:text-white transition-colors">Leaderboards</a>
            <a href="/rules" className="hover:text-white transition-colors">Rules</a>
            <a href="/help" className="hover:text-white transition-colors">Help</a>
            <a href="/legal" className="hover:text-white transition-colors">Legal</a>
          </div>

          {/* Social */}
          <div className="flex items-center gap-4">
            <a
              href="https://discord.gg/gJgHMDZAES"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg glass flex items-center justify-center text-vanta-muted hover:text-white hover:border-[rgba(88,101,242,0.4)] transition-all"
              aria-label="VANTA Discord"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
              </svg>
            </a>
            <a
              href="https://twitter.com/vantagg"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg glass flex items-center justify-center text-vanta-muted hover:text-white transition-all"
              aria-label="VANTA Twitter"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.259 5.631L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
              </svg>
            </a>
            <a
              href="https://reddit.com/r/vantarust"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-lg glass flex items-center justify-center text-vanta-muted hover:text-white transition-all"
              aria-label="VANTA Reddit"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}